import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Stack
} from '@mui/material';
import {
  Pending as PendingIcon,
  CheckCircle as ApprovedIcon,
  Cancel as DeniedIcon,
  Redeem as FulfilledIcon,
  LocalOffer as PriceIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { PrizeRedemption, Prize, User } from '../../types';
import { prizeService } from '../../services/prizeService';
import { useAuth } from '../../context/AuthContext';
import { userService } from '../../services/userService';

interface CoachRedemptionsTabProps {
  teamId: string | null;
  onRedemptionUpdated: () => void;
}

const CoachRedemptionsTab: React.FC<CoachRedemptionsTabProps> = ({
  teamId,
  onRedemptionUpdated
}) => {
  const { user } = useAuth();
  const [redemptions, setRedemptions] = useState<PrizeRedemption[]>([]);
  const [prizes, setPrizes] = useState<{ [key: string]: Prize }>({});
  const [athletes, setAthletes] = useState<{ [key: string]: User }>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedRedemption, setSelectedRedemption] = useState<PrizeRedemption | null>(null);
  const [coachNotes, setCoachNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const [updatingRedemptionId, setUpdatingRedemptionId] = useState<string | null>(null);

  useEffect(() => {
    if (teamId) {
      loadRedemptions();
    }
  }, [teamId]);

  const loadRedemptions = async () => {
    if (!teamId) return;

    setLoading(true);
    setError('');

    try {
      const redemptionsData = await prizeService.getRedemptionsByTeam(teamId);
      setRedemptions(redemptionsData);

      // Load prize details for each redemption
      const prizePromises = redemptionsData.map(redemption =>
        prizeService.getPrizeById(redemption.prizeId)
      );
      const prizeResults = await Promise.all(prizePromises);
      
      const prizeMap: { [key: string]: Prize } = {};
      prizeResults.forEach((prize, index) => {
        if (prize) {
          prizeMap[redemptionsData[index].prizeId] = prize;
        }
      });
      setPrizes(prizeMap);

      // Load athlete details for each redemption
      const athletePromises = redemptionsData.map(redemption =>
        userService.getUserById(redemption.userId)
      );
      const athleteResults = await Promise.all(athletePromises);
      
      const athleteMap: { [key: string]: User } = {};
      athleteResults.forEach((athlete, index) => {
        if (athlete) {
          athleteMap[redemptionsData[index].userId] = athlete;
        }
      });
      setAthletes(athleteMap);
    } catch (err: any) {
      console.error('Error loading redemptions:', err);
      setError(err.message || 'Failed to load redemptions');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'denied': return 'error';
      case 'fulfilled': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <PendingIcon />;
      case 'approved': return <ApprovedIcon />;
      case 'denied': return <DeniedIcon />;
      case 'fulfilled': return <FulfilledIcon />;
      default: return <PendingIcon />;
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const handleStatusChange = (redemption: PrizeRedemption) => {
    setSelectedRedemption(redemption);
    setCoachNotes(redemption.notes || '');
    setStatusDialogOpen(true);
  };

  const runAction = async (
    action: () => Promise<void>,
    successLabel: string
  ) => {
    if (!selectedRedemption) return;
    setUpdating(true);
    setError('');
    try {
      await action();
      setStatusDialogOpen(false);
      setSelectedRedemption(null);
      setCoachNotes('');
      setSuccessMessage(successLabel);
      loadRedemptions();
      onRedemptionUpdated();
    } catch (err: any) {
      console.error('Error updating redemption status:', err);
      setError(err.message || 'Failed to update redemption status');
    } finally {
      setUpdating(false);
    }
  };

  const handleApprove = () => {
    if (!selectedRedemption) return;
    runAction(
      () => prizeService.approveRedemption(selectedRedemption.id, coachNotes),
      'Redemption approved.'
    );
  };

  const handleDeny = () => {
    if (!selectedRedemption) return;
    runAction(
      () => prizeService.denyRedemption(selectedRedemption.id, coachNotes),
      'Redemption denied. Wallet points have been refunded if applicable.'
    );
  };

  const handleFulfill = () => {
    if (!selectedRedemption) return;
    runAction(
      () => prizeService.fulfillRedemption(selectedRedemption.id, coachNotes),
      'Redemption marked as fulfilled.'
    );
  };

  const handleCancelStatusChange = () => {
    setStatusDialogOpen(false);
    setSelectedRedemption(null);
    setCoachNotes('');
  };

  const runInlineAction = async (
    redemption: PrizeRedemption,
    action: () => Promise<void>,
    successLabel: string
  ) => {
    setUpdatingRedemptionId(redemption.id);
    setError('');
    try {
      await action();
      setSuccessMessage(successLabel);
      loadRedemptions();
      onRedemptionUpdated();
    } catch (err: any) {
      console.error('Error updating redemption status:', err);
      setError(err.message || 'Failed to update redemption status');
    } finally {
      setUpdatingRedemptionId(null);
    }
  };

  const handleApproveRedemption = (redemption: PrizeRedemption) => {
    runInlineAction(
      redemption,
      () => prizeService.approveRedemption(redemption.id, ''),
      'Redemption approved.'
    );
  };

  const handleDenyRedemption = (redemption: PrizeRedemption) => {
    runInlineAction(
      redemption,
      () => prizeService.denyRedemption(redemption.id, ''),
      'Redemption denied. Wallet points have been refunded if applicable.'
    );
  };

  const handleFulfillRedemption = (redemption: PrizeRedemption) => {
    runInlineAction(
      redemption,
      () => prizeService.fulfillRedemption(redemption.id, ''),
      'Redemption marked as fulfilled.'
    );
  };

  const renderActionButtons = (redemption: PrizeRedemption) => {
    const busy = updatingRedemptionId === redemption.id;
    if (redemption.status === 'pending') {
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            variant="contained"
            color="success"
            onClick={() => handleApproveRedemption(redemption)}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <ApprovedIcon />}
          >
            Approve
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => handleDenyRedemption(redemption)}
            disabled={busy}
            startIcon={<DeniedIcon />}
          >
            Deny
          </Button>
        </Stack>
      );
    }
    if (redemption.status === 'approved') {
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => handleFulfillRedemption(redemption)}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <FulfilledIcon />}
          >
            Mark as Fulfilled
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => handleDenyRedemption(redemption)}
            disabled={busy}
            startIcon={<DeniedIcon />}
          >
            Deny (refund)
          </Button>
        </Stack>
      );
    }
    if (redemption.status === 'denied') {
      return (
        <Button
          size="small"
          variant="contained"
          color="success"
          onClick={() => handleApproveRedemption(redemption)}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <ApprovedIcon />}
        >
          Approve
        </Button>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  const pendingRedemptions = redemptions.filter(r => r.status === 'pending');
  const otherRedemptions = redemptions.filter(r => r.status !== 'pending');

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Prize Redemptions Management ({redemptions.length})
      </Typography>

      {redemptions.length === 0 ? (
        <Alert severity="info">
          No prize redemptions found for this team.
        </Alert>
      ) : (
        <>
          {/* Pending Redemptions */}
          {pendingRedemptions.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" color="warning.main" gutterBottom>
                Pending Approval ({pendingRedemptions.length})
              </Typography>
              <List>
                {pendingRedemptions.map((redemption, index) => {
                  const prize = prizes[redemption.prizeId];
                  const athlete = athletes[redemption.userId];
                  
                  return (
                    <React.Fragment key={redemption.id}>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="h6">
                                {prize?.name || 'Unknown Prize'}
                              </Typography>
                              <Chip
                                icon={getStatusIcon(redemption.status)}
                                label={redemption.status.charAt(0).toUpperCase() + redemption.status.slice(1)}
                                color={getStatusColor(redemption.status) as any}
                                size="small"
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {prize?.description || 'Prize details not available'}
                              </Typography>
                              
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <PersonIcon fontSize="small" color="action" />
                                  <Typography variant="body2">
                                    {athlete?.displayName || 'Unknown Athlete'}
                                  </Typography>
                                </Box>
                                
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <PriceIcon fontSize="small" color="primary" />
                                  <Typography variant="body2">
                                    {redemption.pointsSpent} points
                                  </Typography>
                                </Box>
                                
                                <Typography variant="body2" color="text.secondary">
                                  Requested: {formatDate(redemption.requestedAt)}
                                </Typography>
                              </Box>

                              {redemption.notes && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                                  <strong>Athlete Notes:</strong> {redemption.notes}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          {renderActionButtons(redemption)}
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < pendingRedemptions.length - 1 && <Divider />}
                    </React.Fragment>
                  );
                })}
              </List>
            </Box>
          )}

          {/* Other Redemptions */}
          {otherRedemptions.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                All Redemptions ({otherRedemptions.length})
              </Typography>
              <List>
                {otherRedemptions.map((redemption, index) => {
                  const prize = prizes[redemption.prizeId];
                  const athlete = athletes[redemption.userId];
                  
                  return (
                    <React.Fragment key={redemption.id}>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="h6">
                                {prize?.name || 'Unknown Prize'}
                              </Typography>
                              <Chip
                                icon={getStatusIcon(redemption.status)}
                                label={redemption.status.charAt(0).toUpperCase() + redemption.status.slice(1)}
                                color={getStatusColor(redemption.status) as any}
                                size="small"
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {prize?.description || 'Prize details not available'}
                              </Typography>
                              
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <PersonIcon fontSize="small" color="action" />
                                  <Typography variant="body2">
                                    {athlete?.displayName || 'Unknown Athlete'}
                                  </Typography>
                                </Box>
                                
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <PriceIcon fontSize="small" color="primary" />
                                  <Typography variant="body2">
                                    {redemption.pointsSpent} points
                                  </Typography>
                                </Box>
                                
                                <Typography variant="body2" color="text.secondary">
                                  Requested: {formatDate(redemption.requestedAt)}
                                </Typography>
                              </Box>

                              {redemption.approvedAt && (
                                <Typography variant="body2" color="success.main" sx={{ mb: 0.5 }}>
                                  ✓ Approved: {formatDate(redemption.approvedAt)}
                                </Typography>
                              )}
                              {redemption.fulfilledAt && (
                                <Typography variant="body2" color="info.main" sx={{ mb: 0.5 }}>
                                  🎉 Fulfilled: {formatDate(redemption.fulfilledAt)}
                                </Typography>
                              )}

                              {redemption.notes && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                                  <strong>Notes:</strong> {redemption.notes}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          {renderActionButtons(redemption)}
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < otherRedemptions.length - 1 && <Divider />}
                    </React.Fragment>
                  );
                })}
              </List>
            </Box>
          )}
        </>
      )}

      {/* Status Change Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={handleCancelStatusChange}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Manage Redemption
        </DialogTitle>
        
        <DialogContent>
          {selectedRedemption && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {prizes[selectedRedemption.prizeId]?.name || 'Unknown Prize'}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Athlete: {athletes[selectedRedemption.userId]?.displayName || 'Unknown'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Points: {selectedRedemption.pointsSpent}
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="Coach Notes (optional)"
                multiline
                rows={3}
                value={coachNotes}
                onChange={(e) => setCoachNotes(e.target.value)}
                placeholder="Add any notes about this status change..."
                sx={{ mb: 2 }}
              />

              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                {selectedRedemption.status === 'pending' && (
                  <>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleApprove}
                      disabled={updating}
                      startIcon={updating ? <CircularProgress size={20} color="inherit" /> : <ApprovedIcon />}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleDeny}
                      disabled={updating}
                      startIcon={<DeniedIcon />}
                    >
                      Deny
                    </Button>
                  </>
                )}
                {selectedRedemption.status === 'approved' && (
                  <>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleFulfill}
                      disabled={updating}
                      startIcon={updating ? <CircularProgress size={20} color="inherit" /> : <FulfilledIcon />}
                    >
                      Mark as Fulfilled
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleDeny}
                      disabled={updating}
                      startIcon={<DeniedIcon />}
                    >
                      Deny (refund points)
                    </Button>
                  </>
                )}
                {selectedRedemption.status === 'denied' && (
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleApprove}
                    disabled={updating}
                    startIcon={updating ? <CircularProgress size={20} color="inherit" /> : <ApprovedIcon />}
                  >
                    Approve
                  </Button>
                )}
                {selectedRedemption.status === 'fulfilled' && (
                  <Typography variant="body2" color="text.secondary">
                    No further actions. This redemption is fulfilled.
                  </Typography>
                )}
              </Stack>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancelStatusChange}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
      />
    </Box>
  );
};

export default CoachRedemptionsTab;
